import {
  Building2,
  Copy,
  ExternalLink,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Phone,
  Send,
  User,
  Users,
} from 'lucide-react';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  buildClaimsFilingUrl,
  resolvePremisesLorStep,
} from '@/components/claims-filing/buildClaimsFilingUrl';
import { formatMailingAddress } from '@/components/coverage/propertyContactAddress';
import { ClaimFilingStep } from '@/components/claims-filing/hooks/useClaimFilingState';
import {
  buildPremisesFollowUpUrl,
  getCurrentPremisesFollowUpTask,
  getPremisesFollowUpLabel,
  getPremisesFollowUpStepKey,
} from '@/components/claims-filing/premises/premisesFlow';
import { useCoverageStepProgress } from '@/components/claims-filing/state/useCoverageStepProgress';
import { MakeCallModal } from '@/components/common/calls/make-call-modal/MakeCallModal';
import { SendFrontEmailDialog } from '@/components/matter/SendFrontEmailDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { isCrashingOutEnabledForUser } from '@/lib/featureFlags';
import { cn } from '@/lib/utils';
import { useGetTasksQuery } from '@/services/api';
import { useGetCrashingOutHistoryQuery } from '@/services/api/agentflowService';
import type {
  ReadOrganizationWithEmployees,
  ReadPersonWithRelationships,
} from '@/services/api/generated/subjectsService';
import { usePropertiesWithOwnersQuery } from '@/services/api/subjectsService';
import { Task } from '@/services/types/client-intake-types';
import {
  BenefitDetail,
  BenefitType,
  ClientDecision,
  ContactHandleSchema,
  CoverageCategory,
  CoverageDataSourceInfo,
  CoverageLiabilityStatus,
  type CoverageListItem,
  CoverageSchema,
  CoverageSource,
  CoverageStatus,
  IdentifierType,
  PolicyType,
} from '@/services/types/coverage-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';
import { selectUser } from '@/state/selectors/root-selectors';

import { CoverageLiabilityStatusBadge } from '../CoverageLiabilityStatusBadge';
import { benefitTypeLabels } from './constants';
import { CopyableText } from './CopyableText';
import { CoverageActivitySheet } from './CoverageActivitySheet';
import { CoverageCardDropdownMenu } from './CoverageCardDropdownMenu';
import { CoverageContactModal } from './CoverageContactModal';
import {
  CoverageProgressStepper,
  CoverageStep,
} from './CoverageProgressStepper';
import { CoverageSharingBanner } from './CoverageSharingBanner';
import { CoverageSourceBadge } from './CoverageSourceBadge';
import { DataSourceBadge } from './DataSourceBadge';

/**
 * Get display name from a party that can be either a Person or Organization.
 * - Person: combines first_name and last_name
 * - Organization: uses legal_name or dba_name
 */
const getPartyDisplayName = (
  party: ReadPersonWithRelationships | ReadOrganizationWithEmployees | null | undefined,
): string => {
  if (!party) return 'Unknown';

  // Check if it's an organization (has organization_type or legal_name)
  if ('organization_type' in party || 'legal_name' in party) {
    const org = party as ReadOrganizationWithEmployees;
    return org.legal_name || org.dba_name || 'Unknown Organization';
  }

  // It's a person
  const person = party as ReadPersonWithRelationships;
  const fullName = [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
  return fullName || 'Unknown Person';
};

interface CoverageCardRedesignProps {
  coverage: CoverageListItem;
  onClick?: () => void;
  className?: string;
  matterId: string;
  onDelete?: (coverageId: string) => void;
  onMerge?: (destinationId: string, sourceId: string) => void;
  allCoverages?: CoverageListItem[];
  readOnly?: boolean;
  /** When true, shows a "Pending Client Decision" badge on 1P coverages missing a client_decision */
  needs1PVerification?: boolean;
  forceShowProgressStepper?: boolean;
}

const getPolicyTypeLabel = (policyType: PolicyType | null) => {
  switch (policyType) {
    case PolicyType.AUTO:
      return 'Auto';
    case PolicyType.HEALTH:
      return 'Health';
    case PolicyType.HOMEOWNERS:
      return 'Home';
    case PolicyType.COMMERCIAL:
      return 'Commercial';
    case PolicyType.UMBRELLA:
      return 'Umbrella';
    case PolicyType.GENERAL_LIABILITY:
      return 'GL';
    default:
      return 'Policy';
  }
};

const getPrimaryIdentifier = (coverage: CoverageSchema) => {
  if (
    !coverage.policy_identifiers ||
    coverage.policy_identifiers.length === 0
  ) {
    return null;
  }
  const policyNumber = coverage.policy_identifiers.find(
    (id) => id.identifier_type === IdentifierType.POLICY_NUMBER,
  );
  return policyNumber || coverage.policy_identifiers[0];
};

const getPrimaryClaimNumber = (
  coverage: CoverageSchema,
): {
  claimNumber: string;
  source: CoverageDataSourceInfo | null | undefined;
} | null => {
  const { claims } = coverage;
  if (!claims || claims.length === 0) return null;
  const withIdentifier = claims.find((c) => Boolean(c.claim_identifier));
  if (!withIdentifier?.claim_identifier) return null;
  return {
    claimNumber: withIdentifier.claim_identifier,
    source: withIdentifier.source,
  };
};

const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// Format limit value with K/M suffix for compactness
export const formatLimitCompact = (value: number): string => {
  if (value >= 1000000) {
    const num = value / 1000000;
    return `$${num % 1 === 0 ? num.toFixed(0) : parseFloat(num.toFixed(2))}M`;
  }
  if (value >= 1000) {
    const num = value / 1000;
    return `$${num % 1 === 0 ? num.toFixed(0) : parseFloat(num.toFixed(2))}K`;
  }
  return `$${value.toLocaleString()}`;
};

// Get all benefits with limits for display
export const getBenefitsWithLimits = (
  coverage: CoverageSchema,
): {
  benefit: BenefitDetail;
  limits: Array<{ key: string; value: number }>;
}[] => {
  if (!coverage.benefits) return [];

  return coverage.benefits
    .filter((b) => b.limitations && Object.keys(b.limitations).length > 0)
    .map((benefit) => ({
      benefit,
      limits: Object.entries(benefit.limitations || {}).map(([key, value]) => ({
        key,
        value,
      })),
    }));
};

// Get the primary per-person limit for display (highest per-person limit across benefits)
// Benefits don't stack, so we show per-person limits which are what actually matters
export const getPrimaryPerPersonLimit = (
  coverage: CoverageSchema,
  allowedBenefitTypes?: ReadonlySet<BenefitType>,
): { value: number; count: number } | null => {
  if (!coverage.benefits) return null;

  const perPersonLimits: number[] = [];
  let totalLimitCount = 0;

  coverage.benefits.forEach((benefit) => {
    if (allowedBenefitTypes && !allowedBenefitTypes.has(benefit.benefit_type)) return;
    if (benefit.limitations) {
      totalLimitCount += Object.keys(benefit.limitations).length;

      Object.entries(benefit.limitations).forEach(([key, value]) => {
        const normalizedKey = key.toLowerCase().replace(/_/g, ' ');
        if (
          normalizedKey.includes('per person') ||
          normalizedKey === 'person' ||
          normalizedKey === 'each person'
        ) {
          perPersonLimits.push(value);
        }
      });
    }
  });

  if (perPersonLimits.length === 0) return null;

  // Return the highest per-person limit (most relevant for coverage analysis)
  return {
    value: Math.max(...perPersonLimits),
    count: totalLimitCount,
  };
};

// Count total number of limits
export const getTotalLimitCount = (coverage: CoverageSchema, allowedBenefitTypes?: ReadonlySet<BenefitType>): number => {
  if (!coverage.benefits) return 0;

  let count = 0;
  coverage.benefits.forEach((benefit) => {
    if (allowedBenefitTypes && !allowedBenefitTypes.has(benefit.benefit_type)) return;
    if (benefit.limitations) {
      count += Object.keys(benefit.limitations).length;
    }
  });
  return count;
};

// Helper to categorize handles for an adjuster
const categorizeHandles = (handles: ContactHandleSchema[] | null) => {
  const allHandles = handles || [];
  const phoneHandles = allHandles.filter(
    (h) =>
      h.handle_type?.toLowerCase() === 'phone' ||
      h.handle_type?.toLowerCase() === 'mobile' ||
      h.handle_type?.toLowerCase() === 'work_phone',
  );
  const emailHandles = allHandles.filter(
    (h) =>
      h.handle_type?.toLowerCase() === 'email' ||
      h.handle_type?.toLowerCase() === 'work_email',
  );
  const webHandles = allHandles.filter(
    (h) =>
      h.handle_type?.toLowerCase() === 'website' ||
      h.handle_type?.toLowerCase() === 'web' ||
      h.handle_type?.toLowerCase() === 'portal',
  );
  const faxHandles = allHandles.filter(
    (h) => h.handle_type?.toLowerCase() === 'fax',
  );
  return {
    phoneHandles,
    emailHandles,
    webHandles,
    faxHandles,
    hasContacts: allHandles.length > 0,
  };
};

// Get all adjusters with their contact info
const getAllAdjustersInfo = (coverage: CoverageSchema) => {
  if (!coverage.adjusters || coverage.adjusters.length === 0) return null;

  return coverage.adjusters.map((adjuster) => {
    const { phoneHandles, emailHandles, webHandles, faxHandles, hasContacts } =
      categorizeHandles(adjuster.handles);
    return {
      name: adjuster.name || 'Unknown',
      benefitType: adjuster.benefit_type
        ? benefitTypeLabels[adjuster.benefit_type]
        : null,
      phones: phoneHandles,
      emails: emailHandles,
      websites: webHandles,
      faxes: faxHandles,
      hasContacts,
      adjuster,
    };
  });
};

export const CoverageCardRedesign = ({
  coverage,
  onClick,
  className,
  matterId,
  onDelete,
  onMerge,
  allCoverages,
  readOnly,
  needs1PVerification,
  forceShowProgressStepper,
}: CoverageCardRedesignProps) => {
  const navigate = useNavigate();

  const isShared = coverage.source === CoverageSource.SHARED;
  const isReadOnly = readOnly ?? isShared;

  // Check if a crashing out agent is running for this coverage
  const user = useSelector(selectUser);
  const crashoutEnabled = isCrashingOutEnabledForUser(user?.email);
  const { data: historyData } = useGetCrashingOutHistoryQuery(
    { matterId, coverageId: coverage.id },
    { skip: !crashoutEnabled, pollingInterval: 30_000 },
  );
  const agentRunning = crashoutEnabled && historyData?.has_running;

  // Modal states for phone and email
  const [callModalOpen, setCallModalOpen] = React.useState(false);
  const [callModalData, setCallModalData] = React.useState<{
    phoneNumber: string;
    contactLabel: string;
  } | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);
  const [emailDialogData, setEmailDialogData] = React.useState<{
    email: string;
  } | null>(null);
  const [contactModalOpen, setContactModalOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [activitySheetOpen, setActivitySheetOpen] = React.useState(false);

  // Handler for phone click
  const handlePhoneClick = React.useCallback(
    (phoneNumber: string, contactLabel?: string) => {
      setCallModalData({
        phoneNumber,
        contactLabel: contactLabel || coverage.carrier?.name || 'Contact',
      });
      setCallModalOpen(true);
    },
    [coverage.carrier?.name],
  );

  // Handler for email click
  const handleEmailClick = React.useCallback((email: string) => {
    setEmailDialogData({ email });
    setEmailDialogOpen(true);
  }, []);

  // Handler for web click
  const handleWebClick = React.useCallback((url: string) => {
    let fullUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      fullUrl = `https://${url}`;
    }
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }, []);

  // Handler for copy
  const handleCopy = React.useCallback((value: string, label: string) => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied to clipboard`);
  }, []);

  // Get step progress for this coverage
  const {
    reviewDone,
    callDone,
    lorDone: lorDoneFromHook,
    reviewSendDone: reviewSendDoneFromHook,
    ackDone,
    sendLorPremisesDone: sendLorPremisesDoneFromHook,
    sendLorPremisesNA: sendLorPremisesNAFromHook,
    reviewSendPremisesDone,
    premisesFollowUpDone,
    premisesFollowUpNA,
  } = useCoverageStepProgress(matterId, coverage.id);

  // Fetch property details for PROPERTY category coverages
  const { data: propertyWithOwners } = usePropertiesWithOwnersQuery(
    {
      id: coverage.property_id ?? '',
      matterId,
    },
    {
      skip: !coverage.property_id || coverage.category !== CoverageCategory.PROPERTY,
    },
  );

  // Get tasks for this coverage
  const { data: tasksResponse } = useGetTasksQuery({
    matter: matterId,
    limit: 200,
  });

  const matterTasks: Task[] = React.useMemo(() => {
    return ((tasksResponse?.results as Task[]) || []) as Task[];
  }, [tasksResponse?.results]);

  const coverageTasks: Task[] = React.useMemo(() => {
    return matterTasks.filter(
      (t) =>
        (t as unknown as { coverage_id?: string }).coverage_id ===
        coverage.id || t.coverage?.id === coverage.id,
    );
  }, [matterTasks, coverage.id]);

  const getTaskByType = React.useCallback(
    (type: CanonicalTaskType) =>
      coverageTasks.find((t) => t.type?.canonical_type === type),
    [coverageTasks],
  );

  const isTaskDone = (task?: Task) => {
    if (!task) return false;
    // A task is considered done if:
    // 1. canonical_status is COMPLETED, OR
    // 2. completed_at timestamp is set (fallback for data inconsistencies)
    const status: CanonicalTaskStatus | null =
      task?.status?.canonical_status ?? null;
    return (
      status === CanonicalTaskStatus.COMPLETED || Boolean(task.completed_at)
    );
  };

  const isTaskNA = (task?: Task) =>
    (task?.status?.canonical_status ?? null) ===
    CanonicalTaskStatus.NOT_APPLICABLE;

  // Get tasks for steps
  const find1pTasks = React.useMemo(
    () =>
      matterTasks.filter(
        (t) => t.type?.canonical_type === CanonicalTaskType.FIND_1P_COVERAGE,
      ),
    [matterTasks],
  );
  const find3pTasks = React.useMemo(
    () =>
      matterTasks.filter(
        (t) => t.type?.canonical_type === CanonicalTaskType.FIND_3P_COVERAGE,
      ),
    [matterTasks],
  );
  const reviewTask = getTaskByType(CanonicalTaskType.REVIEW_FOUND_COVERAGE);
  const callTask = getTaskByType(CanonicalTaskType.OPEN_CLAIM);
  const lorTask = getTaskByType(CanonicalTaskType.SEND_LOR);
  const ackTask = getTaskByType(CanonicalTaskType.LOR_ACKNOWLEDGEMENT);
  const limitsTask3P = getTaskByType(CanonicalTaskType.GET_LIABILITY_LIMITS);
  const decPageTask1P = getTaskByType(CanonicalTaskType.DECLARATION_PAGE);
  const limitsTask =
    coverage.category === CoverageCategory.FIRST_PARTY
      ? decPageTask1P
      : limitsTask3P;

  // Derive completion states
  // A task is considered done if canonical_status is COMPLETED/NA or completed_at is set
  const isDiscoveryTaskDone = (t: Task) =>
    t.status?.canonical_status === CanonicalTaskStatus.COMPLETED ||
    t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE ||
    Boolean(t.completed_at);

  const reviewIsDone = React.useMemo(() => {
    if (coverage.category === CoverageCategory.FIRST_PARTY) {
      return find1pTasks.some(isDiscoveryTaskDone);
    }
    if (coverage.category === CoverageCategory.THIRD_PARTY) {
      return find3pTasks.some(isDiscoveryTaskDone);
    }
    return isTaskDone(reviewTask) || isTaskNA(reviewTask) || reviewDone;
  }, [coverage.category, find1pTasks, find3pTasks, reviewTask, reviewDone]);

  const fileDoneOrNA = isTaskDone(callTask) || isTaskNA(callTask) || callDone;
  const lorDoneOrNA =
    isTaskDone(lorTask) || isTaskNA(lorTask) || lorDoneFromHook;
  const reviewSendDoneOrNA =
    isTaskDone(lorTask) || isTaskNA(lorTask) || reviewSendDoneFromHook;
  const ackDoneOrNA = isTaskDone(ackTask) || isTaskNA(ackTask) || ackDone;
  const limitsDoneOrNA = limitsTask
    ? isTaskDone(limitsTask) || isTaskNA(limitsTask)
    : false;

  // Premises-specific task lookups (matter-level, not coverage-linked)
  const isPremisesCategory = coverage.category === CoverageCategory.PROPERTY;

  const confirmLocationTask = React.useMemo(
    () =>
      matterTasks.find(
        (t) =>
          t.type?.canonical_type ===
          CanonicalTaskType.REVIEW_INCIDENT_PROPERTIES,
      ),
    [matterTasks],
  );
  const researchPropertyTask = React.useMemo(
    () =>
      coverageTasks.find(
        (t) =>
          t.type?.canonical_type === CanonicalTaskType.RESEARCH_PROPERTY_OWNERS,
      ),
    [coverageTasks],
  );
  const premisesFollowUpTask = React.useMemo(
    () =>
      isPremisesCategory ? getCurrentPremisesFollowUpTask(coverageTasks) : undefined,
    [isPremisesCategory, coverageTasks],
  );
  const premisesFollowUpKey = getPremisesFollowUpStepKey(premisesFollowUpTask);
  const premisesFollowUpLabel = getPremisesFollowUpLabel(premisesFollowUpTask);
  const premisesFollowUpHref = buildPremisesFollowUpUrl(
    matterId,
    coverage.id,
    premisesFollowUpTask,
  );

  // Premises task completion states
  const confirmLocationDoneOrNA =
    isTaskDone(confirmLocationTask) || isTaskNA(confirmLocationTask);
  const researchPropertyDoneOrNA =
    isTaskDone(researchPropertyTask) || isTaskNA(researchPropertyTask);
  const sendLorPremisesDoneOrNA =
    sendLorPremisesDoneFromHook || sendLorPremisesNAFromHook;

  // Build steps for stepper
  const buildLink = (step: ClaimFilingStep) =>
    buildClaimsFilingUrl(matterId, coverage.id, step);
  const buildPremisesLorLink = () =>
    buildLink(
      resolvePremisesLorStep({
        lorDone: sendLorPremisesDoneOrNA,
        reviewSendDone: reviewSendPremisesDone,
      }),
    );

  // Build steps with done status first - premises vs standard workflow
  const doneStatuses = isPremisesCategory
    ? [
      confirmLocationDoneOrNA,
      researchPropertyDoneOrNA,
      sendLorPremisesDoneOrNA,
      ...(premisesFollowUpTask ? [premisesFollowUpDone || premisesFollowUpNA] : []),
    ]
    : [
      reviewIsDone,
      fileDoneOrNA,
      reviewSendDoneOrNA,
      ackDoneOrNA,
      limitsDoneOrNA,
    ];

  // Find the furthest completed step index (-1 if none done)
  const furthestDoneIndex = doneStatuses.reduce(
    (maxIdx, done, idx) => (done ? idx : maxIdx),
    -1,
  );

  // The "next" step is the one after the furthest completed step
  // This ensures the highlight matches the progress bar position
  const nextStepIndex = furthestDoneIndex + 1;

  // Premises-specific steps
  const premisesSteps: CoverageStep[] = [
    {
      key: 'confirm_location',
      label: 'Review Incident Properties',
      done: confirmLocationDoneOrNA,
      isNext: nextStepIndex === 0,
      href: `/matters/${matterId}/premises/${coverage.id}/confirm-location`,
    },
    {
      key: 'research_property',
      label: 'Research Property & Owners',
      done: researchPropertyDoneOrNA,
      isNext: nextStepIndex === 1,
      href: `/matters/${matterId}/premises/${coverage.id}/research-property`,
    },
    {
      key: 'send_lor_premises',
      label: 'Write a Letter of Representation',
      done: sendLorPremisesDoneOrNA,
      isNext: nextStepIndex === 2,
      href: buildPremisesLorLink(),
    },
  ];

  if (premisesFollowUpTask && premisesFollowUpKey && premisesFollowUpLabel) {
    premisesSteps.push({
      key: premisesFollowUpKey,
      label: premisesFollowUpLabel,
      done: premisesFollowUpDone || premisesFollowUpNA,
      isNext: nextStepIndex === 3,
      href: premisesFollowUpHref ?? undefined,
    });
  }

  // Standard insurance steps
  const standardSteps: CoverageStep[] = [
    {
      key: 'review',
      label: 'Review Information',
      done: reviewIsDone,
      isNext: nextStepIndex === 0,
      href: buildLink('review'),
    },
    {
      key: 'call',
      label: 'Open Claim',
      done: fileDoneOrNA,
      isNext: nextStepIndex === 1,
      href: buildLink('call'),
    },
    {
      key: 'review_send',
      label: 'Review & Send LOR',
      done: reviewSendDoneOrNA,
      isNext: nextStepIndex === 2,
      href: buildLink('review_send'),
    },
    {
      key: 'ack',
      label: 'Receive LOA',
      done: ackDoneOrNA,
      isNext: nextStepIndex === 3,
      href: buildLink('ack'),
    },
    {
      key: 'limits',
      label:
        coverage.category === CoverageCategory.FIRST_PARTY
          ? 'Declarations Page'
          : 'Find Limits',
      done: limitsDoneOrNA,
      isNext: nextStepIndex === 4,
      href: `/matters/${matterId}/coverage/${coverage.id}/edit?tab=benefits`,
    },
  ];

  const steps = isPremisesCategory ? premisesSteps : standardSteps;

  // Find the current next step for CTA
  const nextStep = steps.find((s) => s.isNext);

  // Determine CTA message and action
  const getCtaInfo = () => {
    if (!nextStep) return null;
    switch (nextStep.key) {
      // Standard insurance steps
      case 'review':
        return {
          message: 'Review coverage information',
          buttonLabel: 'Review',
          href: nextStep.href,
        };
      case 'call':
        return {
          message: 'Ready to open a claim',
          buttonLabel: 'Open Claim',
          href: nextStep.href,
        };
      case 'review_send':
        return {
          message: 'Letter of Representation ready for review',
          buttonLabel: 'Review & Send',
          href: nextStep.href,
        };
      case 'ack':
        return {
          message: 'Waiting for acknowledgement',
          buttonLabel: 'Mark Received',
          href: nextStep.href,
        };
      case 'limits':
        return {
          message: 'Update coverage limits',
          buttonLabel: 'Update Limits',
          href: nextStep.href,
        };
      // Premises-specific steps
      case 'confirm_location':
        return {
          message: 'Review incident properties',
          buttonLabel: 'Review',
          href: `/matters/${matterId}/premises/${coverage.id}/confirm-location`,
        };
      case 'research_property':
        return {
          message: 'Research property and owner contacts',
          buttonLabel: 'Research',
          href: `/matters/${matterId}/premises/${coverage.id}/research-property`,
        };
      case 'send_lor_premises':
        return {
          message: 'Draft the Letter of Representation',
          buttonLabel: 'Draft Letter',
          href: nextStep.href,
        };
      case 'open_premises_claim':
        return {
          message: 'Open the premises claim and record claim details',
          buttonLabel: 'Open Claim',
          href: nextStep.href,
        };
      case 'research_alternate_contacts':
        return {
          message: 'Update alternate property contacts before resending the LOR',
          buttonLabel: 'Update Contacts',
          href: nextStep.href,
        };
      default:
        return null;
    }
  };

  const ctaInfo = getCtaInfo();

  const handleStepClick = (step: CoverageStep) => {
    if (step.href) {
      navigate(step.href);
    }
  };

  // Get display data
  const primaryIdentifier = getPrimaryIdentifier(coverage);
  const primaryClaimNumber = getPrimaryClaimNumber(coverage);
  const isFirstParty = coverage.category === CoverageCategory.FIRST_PARTY;
  const allBenefitsWithLimits = getBenefitsWithLimits(coverage);
  // Show all benefits with limits on the card — filtering by FIRST_PARTY_RELEVANT_BENEFIT_TYPES
  // previously caused 1P cards to show "-" when only BI/PD limits were populated (e.g. UM/UIM
  // exist as benefit entries but have no dollar amounts yet).
  const benefitsWithLimits = allBenefitsWithLimits;
  const perPersonLimit = getPrimaryPerPersonLimit(coverage);
  const totalLimitCount = getTotalLimitCount(coverage);
  const adjustersInfo = getAllAdjustersInfo(coverage);
  const policyTypeLabel = getPolicyTypeLabel(coverage.policy_type);
  const isNotApplicable = coverage.status === CoverageStatus.NOT_APPLICABLE;
  const isHealthCoverage =
    coverage.category === CoverageCategory.FIRST_PARTY_HEALTH;

  // Format physical address for display
  const propertyAddress = React.useMemo(() => {
    if (!propertyWithOwners?.physical_address) return null;
    const addr = propertyWithOwners.physical_address;
    const parts = [
      addr.line1,
      addr.line2,
      [addr.city, addr.state, addr.zip].filter(Boolean).join(', '),
    ].filter(Boolean);
    return parts.join(', ') || null;
  }, [propertyWithOwners?.physical_address]);

  // For premises, show property_name at the top of the card (not carrier.name)
  const premisesDisplayName = React.useMemo(() => {
    if (!isPremisesCategory) return null;
    if (propertyWithOwners?.property_name) {
      return propertyWithOwners.property_name;
    }
    if (propertyAddress) {
      return propertyAddress;
    }
    if (coverage.named_insured && coverage.named_insured.length > 0) {
      return coverage.named_insured[0];
    }
    return 'Unknown Property';
  }, [isPremisesCategory, propertyAddress, propertyWithOwners?.property_name, coverage.named_insured]);

  // Get carrier name for premises coverages (shown in content section)
  const premisesCarrierName = React.useMemo(() => {
    if (!isPremisesCategory) return null;
    return coverage.carrier?.name || null;
  }, [isPremisesCategory, coverage.carrier?.name]);

  // Get property owners for display
  const propertyOwners = React.useMemo(() => {
    if (!propertyWithOwners?.owners) return [];
    return propertyWithOwners.owners.map((ownership) => ({
      id: ownership.id,
      name: getPartyDisplayName(ownership.party),
      type: ownership.ownership_type,
      partyType: ownership.party?.party_type,
    }));
  }, [propertyWithOwners?.owners]);

  return (
    <div
      className={cn(
        'bg-white rounded-xl border border-zinc-200 overflow-hidden',
        isNotApplicable && 'opacity-60',
        className,
      )}
    >
      {/* Sharing Banner (managed-by-primary or shared-to-companions) */}
      {(isShared || (!isShared && coverage.shared_to_count > 0)) && (
        <div className="px-4 py-2.5 border-b border-zinc-200">
          <CoverageSharingBanner coverage={coverage} stopPropagation />
        </div>
      )}

      {/* Header */}
      <div className="px-4 pt-3 pb-2 border-b border-zinc-100">
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={() => setContactModalOpen(true)}
              className="text-base font-semibold text-zinc-900 hover:text-zinc-700 cursor-pointer underline underline-offset-2"
            >
              {isPremisesCategory
                ? premisesDisplayName
                : coverage.carrier?.name || 'Unknown Carrier'}
            </button>
            <Badge
              variant="secondary"
              className="text-[10px] font-normal bg-zinc-100 text-zinc-600 border-0 px-1.5 py-0 h-4"
            >
              {policyTypeLabel}
            </Badge>
            {coverage.status === CoverageStatus.APPROVED && (
              <Badge
                variant="secondary"
                className="text-[10px] font-medium bg-emerald-50 text-emerald-700 border-0 px-1.5 py-0 h-4"
              >
                Approved
              </Badge>
            )}
            {(!coverage.status ||
              coverage.status === CoverageStatus.NEEDS_APPROVAL) && (
                <Badge
                  variant="secondary"
                  className="text-[10px] font-medium bg-amber-50 text-amber-700 border-0 px-1.5 py-0 h-4"
                >
                  Needs Approval
                </Badge>
              )}
            {coverage.status === CoverageStatus.REJECTED && (
              <Badge
                variant="secondary"
                className="text-[10px] font-medium bg-red-50 text-red-700 border-0 px-1.5 py-0 h-4"
              >
                Rejected
              </Badge>
            )}
            {coverage.status === CoverageStatus.NOT_APPLICABLE && (
              <Badge
                variant="secondary"
                className="text-[10px] font-medium bg-zinc-100 text-zinc-500 border-0 px-1.5 py-0 h-4"
              >
                N/A
              </Badge>
            )}
            {coverage.client_decision === ClientDecision.APPROVED && (
              <Badge
                variant="secondary"
                className="text-[10px] font-medium bg-emerald-50 text-emerald-700 border-0 px-1.5 py-0 h-4"
              >
                Client: Approved
              </Badge>
            )}
            {coverage.client_decision === ClientDecision.DECLINED && (
              <Badge
                variant="secondary"
                className="text-[10px] font-medium bg-red-50 text-red-700 border-0 px-1.5 py-0 h-4"
              >
                Client: Declined
              </Badge>
            )}
            {needs1PVerification &&
              !coverage.client_decision &&
              !primaryClaimNumber &&
              (coverage.category === CoverageCategory.FIRST_PARTY ||
                coverage.category === CoverageCategory.FIRST_PARTY_HEALTH) && (
                <Badge
                  variant="secondary"
                  className="text-[10px] font-medium bg-amber-50 text-amber-700 border-0 px-1.5 py-0 h-4"
                >
                  Client: Pending Decision
                </Badge>
              )}
            {coverage.is_primary && (
              <Badge
                variant="secondary"
                className="text-[10px] font-medium bg-blue-50 text-blue-700 border-0 px-1.5 py-0 h-4"
              >
                Primary
              </Badge>
            )}
            {isShared && (
              <Badge
                variant="outline"
                className="border-indigo-200 bg-indigo-50 text-indigo-700 text-[10px] font-medium px-1.5 py-0 h-4 gap-0.5"
              >
                <Lock className="h-2.5 w-2.5" />
                Read Only
              </Badge>
            )}
            <CoverageSourceBadge coverage={coverage} />
            {agentRunning && (
              <Badge
                variant="secondary"
                className="text-[10px] font-medium bg-amber-50 text-amber-700 border-0 px-1.5 py-0 h-4 gap-0.5 animate-pulse"
              >
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
                Agent Running
              </Badge>
            )}
            {coverage.liability_status && (
              <CoverageLiabilityStatusBadge
                liabilityStatus={coverage.liability_status}
                denialReason={coverage.denial_reason}
                showLabel={false}
              />
            )}
          </div>
          <CoverageCardDropdownMenu
            coverage={coverage}
            matterId={matterId}
            isReadOnly={isReadOnly}
            onClick={onClick}
            allCoverages={allCoverages}
            onMerge={onMerge}
            onActivityOpen={() => setActivitySheetOpen(true)}
            onDeleteOpen={() => setDeleteDialogOpen(true)}
          />
        </div>
      </div>

      {/* 2-Column Content */}
      <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-[13px]">
        {/* Column 1: Identity (or Property Info for Premises) */}
        <div className="space-y-1.5">
          {/* Premises-specific: Show carrier and property address */}
          {isPremisesCategory && premisesCarrierName && (
            <div className="flex">
              <span className="text-zinc-600 font-medium w-28 flex-shrink-0 flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                Carrier:
              </span>
              <CopyableText
                value={premisesCarrierName}
                className="text-[13px] text-zinc-900 font-medium"
              />
            </div>
          )}
          {isPremisesCategory && propertyAddress && propertyAddress !== premisesDisplayName && (
            <div className="flex">
              <span className="text-zinc-600 font-medium w-28 flex-shrink-0 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Location:
              </span>
              <CopyableText
                value={propertyAddress}
                className="text-[13px] text-zinc-900 font-medium text-left"
              />
            </div>
          )}
          {isPremisesCategory && propertyOwners.length > 0 && (
            <div className="flex">
              <span className="text-zinc-600 font-medium w-28 flex-shrink-0 flex items-center gap-1">
                <Users className="h-3 w-3" />
                Owners:
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-zinc-900 font-medium hover:text-zinc-700 cursor-pointer underline underline-offset-2 decoration-dotted"
                  >
                    {propertyOwners.length} {propertyOwners.length === 1 ? 'owner' : 'owners'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3" align="start">
                  <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
                    Property Owners
                  </div>
                  <div className="space-y-2">
                    {propertyOwners.map((owner) => (
                      <div key={owner.id} className="flex items-center gap-2">
                        {owner.partyType === 'organization' ? (
                          <Building2 className="h-3.5 w-3.5 text-zinc-400" />
                        ) : (
                          <User className="h-3.5 w-3.5 text-zinc-400" />
                        )}
                        <span className="text-sm text-zinc-900">{owner.name}</span>
                        <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4 capitalize">
                          {owner.type}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
          {isPremisesCategory && coverage.property_contacts && coverage.property_contacts.length > 0 && (() => {
            const contact = coverage.property_contacts![0];
            const contactPhone = contact.handles?.find(h => h.handle_type === 'PHONE');
            const contactEmail = contact.handles?.find(h => h.handle_type === 'EMAIL');
            const formattedAddress = formatMailingAddress(contact.mailing_address);
            const typeLabel = contact.contact_type === 'PROPERTY_OWNER' ? 'Owner'
              : contact.contact_type === 'PROPERTY_MANAGER' ? 'Manager' : contact.custom_type_label || 'Other';
            return (
              <div className="flex">
                <span className="text-zinc-600 font-medium w-28 flex-shrink-0 flex items-center gap-1">
                  <User className="h-3 w-3" />
                  Contact:
                </span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 text-zinc-900 font-medium hover:text-zinc-700 cursor-pointer underline underline-offset-2 decoration-dotted"
                    >
                      <span className="truncate">{contact.name || 'Unnamed'}</span>
                      <span className="text-zinc-500 no-underline flex-shrink-0">
                        ({typeLabel})
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" align="start">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-zinc-100">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-50">
                        <User className="h-4 w-4 text-violet-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-zinc-900">
                          {contact.name}
                        </div>
                        <div className="text-xs text-zinc-500">{typeLabel}</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {contactPhone && (
                        <div className="flex items-center gap-2 text-xs text-zinc-700">
                          <Phone className="h-3.5 w-3.5 text-zinc-400" />
                          <span>{contactPhone.value}</span>
                        </div>
                      )}
                      {contactEmail && (
                        <div className="flex items-center gap-2 text-xs text-zinc-700">
                          <Mail className="h-3.5 w-3.5 text-zinc-400" />
                          <span className="truncate">{contactEmail.value}</span>
                        </div>
                      )}
                      {formattedAddress && (
                        <div className="flex items-center gap-2 text-xs text-zinc-700">
                          <MapPin className="h-3.5 w-3.5 text-zinc-400 flex-shrink-0" />
                          <span>{formattedAddress}</span>
                        </div>
                      )}
                      {coverage.property_contacts!.length > 1 && (
                        <div className="text-xs text-zinc-500 pt-1 border-t border-zinc-100">
                          +{coverage.property_contacts!.length - 1} more contact{coverage.property_contacts!.length > 2 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            );
          })()}
          {/* Standard coverage fields */}
          {!isPremisesCategory && coverage.named_insured && coverage.named_insured.length > 0 && (
            <div className="flex">
              <span className="text-zinc-600 font-medium w-28 flex-shrink-0">
                Insured:
              </span>
              <CopyableText
                value={coverage.named_insured[0]}
                className="text-[13px] text-zinc-900 font-medium"
              />
            </div>
          )}
          {primaryIdentifier && (
            <div className="flex">
              <span className="text-zinc-600 font-medium w-28 flex-shrink-0">
                Policy Number:
              </span>
              <CopyableText
                value={primaryIdentifier.identifier_value}
                className="text-[13px] text-zinc-900 font-medium"
                mono
              />
              <DataSourceBadge
                source={primaryIdentifier.source}
                className="ml-1"
              />
            </div>
          )}
          {primaryClaimNumber && (
            <div className="flex">
              <span className="text-zinc-600 font-medium w-28 flex-shrink-0">
                Claim #:
              </span>
              <CopyableText
                value={primaryClaimNumber.claimNumber}
                className="text-[13px] text-zinc-900 font-medium"
                mono
              />
              <DataSourceBadge
                source={primaryClaimNumber.source}
                className="ml-1"
              />
            </div>
          )}
        </div>

        {/* Column 2: Coverage & Contacts */}
        <div className="space-y-1.5">
          {/* Coverage Limit with Popover - shows per-person limit since benefits don't stack */}
          <div className="flex">
            <span className="text-zinc-600 font-medium w-28 flex-shrink-0">
              Coverage Limit:
            </span>
            {totalLimitCount > 0 ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-zinc-900 font-medium hover:text-zinc-700 cursor-pointer underline underline-offset-2 decoration-dotted"
                  >
                    {perPersonLimit ? (
                      <>
                        {formatLimitCompact(perPersonLimit.value)}/person (
                        {perPersonLimit.count})
                      </>
                    ) : (
                      <>({totalLimitCount} limits)</>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3" align="start">
                  <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
                    Policy Limits Breakdown
                  </div>
                  <div className="space-y-2">
                    {benefitsWithLimits.map(({ benefit, limits }) => (
                      <div key={benefit.benefit_type}>
                        <div className="text-xs font-medium text-zinc-700 mb-0.5">
                          {benefitTypeLabels[benefit.benefit_type]}
                        </div>
                        {limits.map((limit) => (
                          <div
                            key={limit.key}
                            className="flex justify-between text-xs text-zinc-600 pl-2"
                          >
                            <span className="capitalize">
                              {limit.key.replace(/_/g, ' ')}
                            </span>
                            <span className="font-medium tabular-nums">
                              {formatLimitCompact(limit.value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <span className="text-zinc-400">-</span>
            )}
          </div>

          {/* Policy Details with popover */}
          {coverage.policy_details && coverage.policy_details.length > 0 && (
            <div className="flex">
              <span className="text-zinc-600 font-medium w-28 flex-shrink-0">
                Policy Details:
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-zinc-900 font-medium hover:text-zinc-700 cursor-pointer underline underline-offset-2 decoration-dotted"
                  >
                    {coverage.policy_details.length === 1
                      ? coverage.policy_details[0].key
                      : `${coverage.policy_details.length} details`}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3" align="start">
                  <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
                    Additional Policy Details
                  </div>
                  <div className="space-y-2">
                    {coverage.policy_details.map((detail, idx) => (
                      <div
                        key={`policy-detail-${idx}`}
                        className="flex justify-between text-xs text-zinc-600"
                      >
                        <span className="font-medium text-zinc-700">
                          {detail.key}
                        </span>
                        <span className="text-zinc-900">{detail.value}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Adjusters with contact popovers - show all adjusters */}
          {adjustersInfo && adjustersInfo.length > 0 ? (
            adjustersInfo.map((adjusterInfo, adjusterIdx) => (
              <div key={`adjuster-row-${adjusterIdx}`} className="flex">
                <span className="text-zinc-600 font-medium w-28 flex-shrink-0">
                  {adjusterIdx === 0 ? 'Adjuster:' : ''}
                </span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 text-zinc-900 font-medium hover:text-zinc-700 cursor-pointer underline underline-offset-2 decoration-dotted max-w-[240px] truncate"
                    >
                      <span className="truncate">{adjusterInfo.name}</span>
                      {adjusterInfo.benefitType && (
                        <span className="text-zinc-500 no-underline flex-shrink-0">
                          ({adjusterInfo.benefitType})
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" align="start">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-zinc-100">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-50">
                        <User className="h-4 w-4 text-violet-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-zinc-900">
                          {adjusterInfo.name}
                        </div>
                        {adjusterInfo.benefitType && (
                          <div className="text-xs text-zinc-500">
                            {adjusterInfo.benefitType}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {/* Phone Numbers */}
                      {adjusterInfo.phones.map((handle, idx) => (
                        <div
                          key={`phone-${idx}`}
                          className="flex items-center justify-between group"
                        >
                          <div className="flex items-center gap-2 text-xs text-zinc-700">
                            <Phone className="h-3.5 w-3.5 text-zinc-400" />
                            <span>{formatPhoneNumber(handle.value)}</span>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() =>
                                handleCopy(handle.value || '', 'Phone')
                              }
                              className="p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded"
                              title="Copy"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handlePhoneClick(
                                  handle.value || '',
                                  adjusterInfo.name,
                                )
                              }
                              className="p-1 text-violet-500 hover:text-violet-700 hover:bg-violet-50 rounded"
                              title="Call"
                            >
                              <Phone className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Emails */}
                      {adjusterInfo.emails.map((handle, idx) => (
                        <div
                          key={`email-${idx}`}
                          className="flex items-center justify-between group"
                        >
                          <div className="flex items-center gap-2 text-xs text-zinc-700 truncate max-w-[180px]">
                            <Mail className="h-3.5 w-3.5 text-zinc-400 flex-shrink-0" />
                            <span className="truncate">{handle.value}</span>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() =>
                                handleCopy(handle.value || '', 'Email')
                              }
                              className="p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded"
                              title="Copy"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleEmailClick(handle.value || '')
                              }
                              className="p-1 text-violet-500 hover:text-violet-700 hover:bg-violet-50 rounded"
                              title="Send Email"
                            >
                              <Send className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Websites */}
                      {adjusterInfo.websites.map((handle, idx) => (
                        <div
                          key={`web-${idx}`}
                          className="flex items-center justify-between group"
                        >
                          <div className="flex items-center gap-2 text-xs text-zinc-700 truncate max-w-[180px]">
                            <ExternalLink className="h-3.5 w-3.5 text-zinc-400 flex-shrink-0" />
                            <span className="truncate">{handle.value}</span>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() =>
                                handleCopy(handle.value || '', 'URL')
                              }
                              className="p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded"
                              title="Copy"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleWebClick(handle.value || '')}
                              className="p-1 text-violet-500 hover:text-violet-700 hover:bg-violet-50 rounded"
                              title="Open"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Fax */}
                      {adjusterInfo.faxes.map((handle, idx) => (
                        <div
                          key={`fax-${idx}`}
                          className="flex items-center justify-between group"
                        >
                          <div className="flex items-center gap-2 text-xs text-zinc-700">
                            <Phone className="h-3.5 w-3.5 text-zinc-400" />
                            <span>{formatPhoneNumber(handle.value)} (Fax)</span>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() =>
                                handleCopy(handle.value || '', 'Fax')
                              }
                              className="p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded"
                              title="Copy"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* No contact info */}
                      {!adjusterInfo.hasContacts && (
                        <div className="text-xs text-zinc-500 italic py-1">
                          No contact information available
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <DataSourceBadge
                  source={adjusterInfo.adjuster.source}
                  className="ml-0.5"
                />
              </div>
            ))
          ) : (
            <div className="flex">
              <span className="text-zinc-600 font-medium w-28 flex-shrink-0">
                Adjuster:
              </span>
              <span className="text-zinc-400 italic">None</span>
            </div>
          )}

          {/* Subrogation Agency - only for health insurance coverages */}
          {isHealthCoverage && coverage.subrogator?.name && (
            <div className="flex">
              <span className="text-zinc-600 font-medium w-28 flex-shrink-0">
                Subro Agency:
              </span>
              <span className="text-zinc-900 font-medium">
                {coverage.subrogator.name}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Progress Stepper - only show for non-health, non-read-only coverage */}
      {(forceShowProgressStepper || !isReadOnly) && !isHealthCoverage && (
        <div className="px-4 pb-2 pt-1">
          <CoverageProgressStepper
            steps={steps}
            onStepClick={handleStepClick}
          />
        </div>
      )}

      {/* CTA Section - compact */}
      {!isReadOnly && ctaInfo && !isNotApplicable && !isHealthCoverage && (
        <div className="px-4 pb-3 pt-2 border-t border-zinc-100 flex items-center justify-between gap-2">
          <p className="text-[12px] text-zinc-600">{ctaInfo.message}</p>
          <Button
            asChild
            size="sm"
            className="h-7 text-[12px] px-3 bg-violet-600 hover:bg-violet-700 text-white"
          >
            <Link to={ctaInfo.href || '#'}>Get started</Link>
          </Button>
        </div>
      )}

      {/* Phone Call Modal */}
      <MakeCallModal
        isOpen={callModalOpen}
        onClose={() => {
          setCallModalOpen(false);
          setCallModalData(null);
        }}
        initialPhoneNumber={callModalData?.phoneNumber}
        initialContactLabel={callModalData?.contactLabel}
        matterId={matterId}
        source="coverage_card_redesign"
      />

      {/* Email Dialog */}
      <SendFrontEmailDialog
        open={emailDialogOpen}
        onClose={() => {
          setEmailDialogOpen(false);
          setEmailDialogData(null);
        }}
        matterId={matterId}
        defaultTo={emailDialogData?.email ? [emailDialogData.email] : []}
      />

      {/* Coverage Contact Modal */}
      <CoverageContactModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        coverage={coverage}
        matterId={matterId}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Coverage</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium text-zinc-900">
                {coverage.carrier?.name || 'this coverage'}
              </span>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete?.(coverage.id);
                setDeleteDialogOpen(false);
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CoverageActivitySheet
        matterId={matterId}
        coverageId={coverage.id}
        carrierName={coverage.carrier?.name ?? undefined}
        open={activitySheetOpen}
        onOpenChange={setActivitySheetOpen}
      />
    </div>
  );
};
